import { Injectable } from '@angular/core';
import { SecureStorageService } from './secure-storage.service';

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private permissions: any = null;
  private readonly noAccessValue = 'no-access';
  private readonly actionRank: Record<PermissionAction, number> = {
    view: 1,
    create: 2,
    edit: 3,
    delete: 4,
  };
  private readonly permissionCapabilities: Record<string, PermissionAction[]> = {
    admin: ['view', 'create', 'edit', 'delete'],
    creator: ['view', 'create', 'edit'],
    editor: ['view', 'edit'],
    viewer: ['view'],
    [this.noAccessValue]: [],
  };

  constructor(private secureStorage: SecureStorageService) {}

  async load(): Promise<void> {
    try {
      const raw = await this.secureStorage.getItem('permissions');
      this.permissions = JSON.parse(raw || '{}');
    } catch (error) {
      console.error('Failed to load permissions:', error);
      this.permissions = {};
    }
  }

  getPermission(key: string): string {
    return this.permissions?.[key] || this.noAccessValue;
  }

  canAccess(key: string, action: PermissionAction = 'view'): boolean {
    const rawPermission = this.getPermission(key);
    const permissionValue = this.normalizePermissionValue(rawPermission);
    const supportedActions = this.permissionCapabilities[permissionValue] || [];
    if (!supportedActions.includes(action)) {
      return false;
    }

    const actionThreshold = this.actionRank[action];
    return supportedActions.some((supportedAction) => this.actionRank[supportedAction] >= actionThreshold);
  }

  canView(key: string): boolean {
    return this.canAccess(key, 'view');
  }

  canCreate(key: string): boolean {
    return this.canAccess(key, 'create');
  }

  canEdit(key: string): boolean {
    return this.canAccess(key, 'edit');
  }

  canDelete(key: string): boolean {
    return this.canAccess(key, 'delete');
  }

  getPermissionLabel(key: string): string {
    const value = this.normalizePermissionValue(this.getPermission(key));
    return value;
  }

  getAll(): any {
    const safePermissions = this.permissions || {};

    return new Proxy(safePermissions, {
      get: (target, prop: string | symbol) => {
        if (typeof prop !== 'string') {
          return (target as any)[prop];
        }
        return target[prop] ?? this.noAccessValue;
      },
    });
  }

  setAll(permissions: Record<string, string> | null | undefined): void {
    this.permissions = permissions || {};
    this.secureStorage
      .setItem('permissions', JSON.stringify(this.permissions))
      .catch((error) => console.error('Failed to persist permissions to secure storage:', error));
  }

  private normalizePermissionValue(value: string | null | undefined): string {
    const normalized = (value || this.noAccessValue).toString().trim().toLowerCase();
    if (normalized === 'none' || normalized === 'noaccess') {
      return this.noAccessValue;
    }
    return normalized || this.noAccessValue;
  }

}
